/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { api, API } from "../lib/api";
import { postOrQueue } from "../lib/offline";
import { toast } from "sonner";
import { Camera, Loader2, Trash2, Utensils, Sparkles, RefreshCw } from "lucide-react";

const MOMENTS = [["matin", "Matin"], ["midi", "Midi"], ["soir", "Soir"], ["collation", "Collation"]];
export const momentLabel = (m) => Object.fromEntries(MOMENTS)[m] || m;
const LEVEL = { faible: ["Faible", "#5CB85C"], moyen: ["Moyen", "#D4AF5A"], eleve: ["Élevé", "#D9534F"], inconnu: ["—", "#0A1426"] };

/** Analyse IA (GPT-5.4) d'une photo de repas : aliments, glucides/sucre, conseil. */
export function MealAnalysis({ photo, onRefresh, compact }) {
  const pending = photo.analyse_statut === "en_cours";
  useEffect(() => {
    if (!pending || !onRefresh) return;
    const t = setTimeout(onRefresh, 5000);
    return () => clearTimeout(t);
  }, [pending, photo.id]); // eslint-disable-line
  const retry = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try { await api.post(`/meal-photos/${photo.id}/analyze`); toast.info("Analyse relancée"); setTimeout(() => onRefresh?.(), 6000); } catch { toast.error("Relance impossible"); }
  };
  if (pending) return <div data-testid="meal-analysis-pending" className="text-[10px] text-[#0A1426]/60 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Analyse IA en cours…</div>;
  if (!photo.analyse) {
    if (photo.analyse_statut === "echec" || photo.analyse_statut === "indisponible")
      return <button data-testid="meal-analysis-retry" onClick={retry} className="text-[10px] text-[#8A6D2B] font-bold underline flex items-center gap-1"><RefreshCw size={10} /> Analyse indisponible · réessayer</button>;
    return null;
  }
  const a = photo.analyse;
  const [gl, gc] = LEVEL[a.glucides] || LEVEL.inconnu;
  const [sl, sc] = LEVEL[a.sucre] || LEVEL.inconnu;
  return (
    <div data-testid="meal-analysis" className={`text-[#0A1426] ${compact ? "text-[10px]" : "text-[12px]"} leading-snug`}>
      <div className="flex items-center gap-1 font-bold"><Sparkles size={compact ? 10 : 12} className="text-[#D4AF5A]" /> {a.aliments?.length ? a.aliments.join(", ") : a.description}</div>
      {!compact && a.aliments?.length > 0 && <div className="text-[#0A1426]/80 mt-0.5">{a.description}</div>}
      <div className="flex flex-wrap gap-1 mt-1">
        <span className="px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${gc}1f`, color: gc }}>Glucides : {gl}</span>
        <span className="px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${sc}1f`, color: sc }}>Sucre : {sl}</span>
      </div>
      {!compact && a.conseil && <div className="italic text-[#0A1426]/80 mt-1">{a.conseil}</div>}
    </div>
  );
}

/** Compresse l'image côté client (max 1024 px, JPEG 0.72) → data URL. */
export function compressImage(file, max = 1024) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.72));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image")); };
    img.src = url;
  });
}

export const mealImageUrl = (id) => `${API}/meal-photos/${id}/image?auth=${encodeURIComponent(localStorage.getItem("bc_token") || "")}`;

export function MealPhotos({ glycemieMgdl }) {
  const { patient } = useApp();
  const [photos, setPhotos] = useState(() => { try { return JSON.parse(localStorage.getItem("bc_meals") || "[]"); } catch { return []; } });
  const [moment, setMoment] = useState(() => { const h = new Date().getHours(); return h < 11 ? "matin" : h < 16 ? "midi" : "soir"; });
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const load = () => patient && api.get(`/meal-photos?patient_id=${patient.id}`).then(({ data }) => { setPhotos(data); localStorage.setItem("bc_meals", JSON.stringify(data)); }).catch(() => null);
  useEffect(() => { load(); }, [patient?.id]); // eslint-disable-line

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !patient) return;
    setBusy(true);
    try {
      const image_b64 = await compressImage(file);
      const r = await postOrQueue("/meal-photos", { patient_id: patient.id, image_b64, mime: "image/jpeg", moment,
        glycemie_mgdl: glycemieMgdl ? Math.round(glycemieMgdl) : null, client_datetime: new Date().toISOString() });
      if (r._queued) { toast.info("Photo enregistrée hors-ligne. Sera envoyée au retour du réseau."); setPhotos((p) => [{ id: `tmp-${Date.now()}`, moment, datetime: new Date().toISOString(), glycemie_mgdl: glycemieMgdl, _local: image_b64 }, ...p]); }
      else { toast.success("Photo du repas enregistrée"); load(); }
    } catch { toast.error("Photo impossible à lire"); }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/meal-photos/${id}`); toast.success("Photo supprimée"); load(); } catch { toast.error("Suppression impossible"); }
  };

  return (
    <section className="bg-white rounded-3xl border border-[#E4E0D5] p-5" data-testid="meal-photos">
      <div className="flex items-center gap-2 mb-3"><Utensils size={20} className="text-[#D4AF5A]" /><h3 className="font-[Fraunces] text-xl font-bold text-[#0A1426]">Mes repas en photo</h3></div>
      <p className="text-[12px] text-[#0A1426]/70 leading-snug mb-3">Photographiez votre assiette : votre médecin verra ce que vous mangez à côté de vos glycémies pour ajuster vos cibles.</p>
      <div className="flex gap-2 mb-3">
        {MOMENTS.map(([k, l]) => (
          <button key={k} data-testid={`meal-moment-${k}`} onClick={() => setMoment(k)}
            className={`flex-1 h-10 rounded-full text-[12px] font-bold ${moment === k ? "bg-[#0A1426] text-white" : "bg-[#F5F3EE] text-[#0A1426]"}`}>{l}</button>
        ))}
      </div>
      <input ref={input} data-testid="meal-photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
      <button data-testid="meal-photo-btn" disabled={busy} onClick={() => input.current?.click()}
        className="w-full h-14 rounded-full bg-[#D4AF5A] text-[#0A1426] font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={20} /> : <Camera size={22} />} Photographier mon repas
      </button>
      {photos.length > 0 && (
        <div className="flex flex-col gap-2 mt-4" data-testid="meal-gallery">
          {photos.slice(0, 9).map((p) => (
            <div key={p.id} data-testid="meal-photo-item" className="flex gap-3 bg-[#F5F3EE]/60 rounded-2xl p-2">
              <div className="relative w-24 h-24 shrink-0 rounded-xl overflow-hidden bg-[#F5F3EE]">
                <img src={p._local || mealImageUrl(p.id)} alt={`Repas ${momentLabel(p.moment)}`} loading="lazy" className="w-full h-full object-cover" />
                {!p._local && (
                  <button data-testid="meal-photo-delete" aria-label="Supprimer" onClick={() => remove(p.id)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/90 text-[#D9534F] grid place-items-center"><Trash2 size={13} /></button>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <div className="text-[11px] font-bold text-[#0A1426]">
                  {momentLabel(p.moment)} · {new Date(p.datetime).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}{p.glycemie_mgdl ? ` · ${p.glycemie_mgdl} mg/dL` : ""}
                </div>
                {!p._local && <MealAnalysis photo={p} onRefresh={load} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Fiche praticien : dernières photos de repas avec glycémie associée. */
export function MealPhotosCard({ patientId }) {
  const [photos, setPhotos] = useState([]);
  const load = () => api.get(`/meal-photos?patient_id=${patientId}`).then(({ data }) => setPhotos(data)).catch(() => null);
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line
  return (
    <div data-testid="pro-meal-photos" className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
      <h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-3">Repas photographiés ({photos.length}) · analyse IA glucides/sucre</h3>
      {photos.length === 0 ? <div className="text-[12px] text-[#0A1426]/60">Aucune photo de repas.</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {photos.slice(0, 12).map((p) => (
            <div key={p.id} data-testid="pro-meal-photo" className="flex gap-2 bg-[#F5F3EE]/60 rounded-xl p-2">
              <a href={mealImageUrl(p.id)} target="_blank" rel="noreferrer" className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-[#F5F3EE] block">
                <img src={mealImageUrl(p.id)} alt="Repas" loading="lazy" className="w-full h-full object-cover" />
              </a>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold text-[#0A1426]">{momentLabel(p.moment)} {new Date(p.datetime).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}{p.glycemie_mgdl ? ` · ${p.glycemie_mgdl} mg/dL` : ""}</div>
                <MealAnalysis photo={p} onRefresh={load} compact />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
